import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Select,
  message,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  BankOutlined,
  HomeOutlined,
  UserOutlined,
  FileTextOutlined,
  MessageOutlined,
  TeamOutlined,
  FormOutlined,
} from "@ant-design/icons";
import axiosInstance from "../../utils/axios.config";
import moment from "moment";
import locale from "antd/es/date-picker/locale/en_US";
import Swal from "sweetalert2";
import { sessionsService } from "../../services/api.service";
import logger from "../../utils/logger";

const { Option } = Select;
const { TextArea } = Input;

const AddSessionModal = ({
  visible,
  onClose, // Changed from onCancel to onClose to match usage
  onSuccess,
  onSessionAdded,
  caseId,
  clientId,
  defaultDate,
  clientCases = [],
  editMode = false,
  initialData = null,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    case_id: caseId,
    session_type: "جلسة قضائية",
  });
  const [cases, setCases] = useState([]);

  // إضافة دالة لجلب القضايا
  const fetchCases = async () => {
    try {
      const response = await axiosInstance.get("/cases");
      if (response.data && response.data.data) {
        setCases(response.data.data);
      }
    } catch (error) {
      logger.error("Error fetching cases:", error);
      message.error("فشل في جلب قائمة القضايا");
    }
  };

  // استدعاء الدالة عند فتح النافذة
  useEffect(() => {
    if (visible) {
      // إعادة تعيين النموذج
      form.resetFields();

      // تعيين القيم الافتراضية
      let initialValues = {
        case_id: caseId,
        session_type: "جلسة قضائية",
      };

      // إذا كنا في وضع التعديل وبيانات الجلسة متوفرة
      if (editMode && initialData) {
        // استخدام بيانات الجلسة الحالية
        initialValues = {
          case_id: initialData.case_id || caseId,
          session_type: initialData.session_type || "جلسة قضائية",
          title: initialData.title,
          date: initialData.date || initialData.session_date,
          time: initialData.time || initialData.session_time,
          court: initialData.court,
          room: initialData.room,
          judge: initialData.judge,
          notes: initialData.notes,
          decisions: initialData.decisions,
        };
      } else {
        // إضافة التاريخ الافتراضي إذا كان موجوداً
        if (defaultDate) {
          initialValues.date = defaultDate;
        }
      }

      form.setFieldsValue(initialValues);
      fetchCases();
    }
  }, [visible, caseId, defaultDate]);

  // التعامل مع تغيير نوع الجلسة
  const handleSessionTypeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      session_type: value,
    }));

    if (value !== "جلسة قضائية") {
      // إذا كان نوع الجلسة ليس جلسة قضائية، امسح قيمة القضية
      form.setFieldsValue({ case_id: undefined });
    }
  };

  // تحديث دالة handleTimeChange
  const handleTimeChange = (time) => {
    logger.debug("Time changed:", time ? time.format("HH:mm:ss") : null);
    setFormData((prev) => ({ ...prev, time }));
    form.setFieldsValue({ time });
  };

  // تحديث دالة handleDateChange
  const handleDateChange = (date) => {
    logger.debug("Date changed:", date ? date.format("YYYY-MM-DD") : null);
    setFormData((prev) => ({
      ...prev,
      date: moment(date),
    }));
    form.setFieldsValue({ date });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      moment.locale("en");

      // التحقق من وجود القيم قبل التنسيق
      if (!values.date || !values.time) {
        throw new Error("التاريخ والوقت مطلوبان");
      }

      if (values.session_type === "جلسة قضائية" && !values.case_id && !caseId) {
        throw new Error("يجب اختيار القضية للجلسات القضائية");
      }

      // تنسيق البيانات للإرسال
      const formattedValues = {
        ...values,
        date: values.date.format("YYYY-MM-DD"),
        time: values.time.format("HH:mm:ss"),
        // استخدام case_id من النموذج مباشرة
        case_id: values.case_id || caseId,
        // إضافة معرف العميل
        client_id: clientId || (values.case_id && cases.find(c => c.id === values.case_id)?.client_id),
      };

      logger.debug("Submitting session with formatted values:", formattedValues);
      
      // استدعاء خدمة إضافة الجلسة
      if (editMode && initialData?.id) {
          await sessionsService.updateSession(initialData.id, formattedValues);
      } else {
          await sessionsService.addSession(formattedValues);
      }
      
      message.success(editMode ? "تم تحديث الجلسة بنجاح" : "تم إضافة الجلسة بنجاح");
      
      // استدعاء دوال النجاح
      if (onSuccess) onSuccess(formattedValues);
      if (onSessionAdded) onSessionAdded(formattedValues);
      
      form.resetFields();
      if (onClose && typeof onClose === "function") { // Changed from onCancel to onClose with safety check
        onClose(); // Changed from onCancel() to onClose()
      }
    } catch (error) {
      logger.error("Error submitting session:", error);
      message.error(error.message || "حدث خطأ في حفظ الجلسة");
    } finally {
      setLoading(false);
      moment.locale("ar");
    }
  };

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-right text-gray-800 mb-4">
          <CalendarOutlined className="ml-2 text-primary" />
          {editMode ? "تعديل الجلسة" : "إضافة جلسة جديدة"}
        </div>
      }
      open={visible}
      onCancel={onClose} // Changed from onCancel to onClose
      okText={editMode ? "تحديث الجلسة" : "إضافة الجلسة"}
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={loading}
      width="90%"
      style={{ maxWidth: '800px' }}
      dir="rtl"
      className="custom-modal"
      styles={{
        body: {
          maxHeight: "80vh",
          overflow: "auto",
          padding: "20px",
        },
      }}
    >
      <Form
        form={form}
        dir="rtl"
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: "قادمة",
          case_id: caseId,
          session_type: "جلسة قضائية",
          date: null,
          time: null,
        }}
        className="px-4"
        requiredMark={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* الصف الأول: نوع الجلسة + عنوان الجلسة + حالة الجلسة */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:col-span-2">
            {/* 1. نوع الجلسة */}
            <Form.Item
              name="session_type"
              label={
                <span className="text-gray-800 font-semibold">نوع الجلسة</span>
              }
              rules={[{ required: true, message: "الرجاء اختيار نوع الجلسة" }]}
              className="mb-3"
            >
              <Select
                placeholder="اختر نوع الجلسة"
                className="text-right h-11"
                size="large"
                onChange={handleSessionTypeChange}
                value={formData.session_type}
              >
                <Option value="جلسة قضائية">جلسة قضائية</Option>
                <Option value="استشارة قانونية">استشارة قانونية</Option>
                <Option value="اجتماع">اجتماع</Option>
              </Select>
            </Form.Item>

            {/* 2. عنوان الجلسة */}
            <Form.Item
              name="title"
              label={
                <span className="text-gray-800 font-semibold">
                  عنوان الجلسة
                </span>
              }
              rules={[{ required: true, message: "الرجاء إدخال عنوان الجلسة" }]}
              className="mb-3"
            >
              <Input
                prefix={<FormOutlined className="ml-2 text-primary" />}
                placeholder="أدخل عنوان الجلسة"
                className="text-right h-11"
                size="large"
              />
            </Form.Item>

            {/* 3. حالة الجلسة */}
            <Form.Item
              name="status"
              label={
                <span className="text-gray-800 font-semibold">حالة الجلسة</span>
              }
              rules={[{ required: true, message: "الرجاء اختيار حالة الجلسة" }]}
              className="mb-3"
            >
              <Select
                placeholder="اختر حالة الجلسة"
                className="text-right h-11"
                size="large"
              >
                <Option value="قادمة">قادمة</Option>
                <Option value="مكتملة">مكتملة</Option>
                <Option value="ملغية">ملغية</Option>
                <Option value="مؤجلة">مؤجلة</Option>
              </Select>
            </Form.Item>
          </div>

          {/* اختيار القضية (فقط لنوع الجلسة القضائية) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:col-span-2">
            {/* 1. القضية */}
            {formData.session_type === "جلسة قضائية" && (
              <Form.Item
                name="case_id"
                label={
                  <span className="text-gray-800 font-semibold">القضية</span>
                }
                rules={[{ required: true, message: "الرجاء اختيار القضية" }]}
                className="mb-3"
              >
                <Select
                  showSearch
                  placeholder="ابحث عن رقم القضية"
                  className="text-right h-11"
                  size="large"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={cases.map((caseItem) => ({
                    value: caseItem.id,
                    label: `${caseItem.case_number} - ${
                      caseItem.client_name || "بدون عنوان"
                    }`,
                    searchValue: caseItem.case_number, // للبحث في رقم القضية
                  }))}
                  notFoundContent="لا توجد قضايا مطابقة للبحث"
                />
              </Form.Item>
            )}

            {/* 2. تاريخ الجلسة */}
            <Form.Item
              name="date"
              label={
                <span className="text-gray-800 font-semibold">
                  تاريخ الجلسة
                </span>
              }
              rules={[{ required: true, message: "تاريخ الجلسة مطلوب" }]}
              className="mb-3"
            >
              <DatePicker
                className="text-right h-11 w-full"
                format="YYYY/MM/DD"
                locale={locale}
                placeholder="اختر تاريخ الجلسة"
                suffixIcon={<CalendarOutlined className="text-primary" />}
                value={formData.date}
                onChange={handleDateChange}
                size="large"
              />
            </Form.Item>

            {/* 3. وقت الجلسة */}
            <Form.Item
              name="time"
              label={
                <span className="text-gray-800 font-semibold">وقت الجلسة</span>
              }
              rules={[{ required: true, message: "وقت الجلسة مطلوب" }]}
              className="mb-3"
            >
              <TimePicker
                className="text-right h-11 w-full"
                format="HH:mm"
                placeholder="اختر وقت الجلسة"
                value={formData.time}
                onChange={handleTimeChange}
                size="large"
              />
            </Form.Item>
          </div>

          {/* حقول خاصة بالجلسات القضائية */}
          {formData.session_type === "جلسة قضائية" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:col-span-2">
              {/* المحكمة */}
              <Form.Item
                name="court"
                label={
                  <span className="text-gray-800 font-semibold">المحكمة</span>
                }
                className="mb-3"
              >
                <Input
                  prefix={<BankOutlined className="ml-2 text-primary" />}
                  placeholder="أدخل اسم المحكمة"
                  className="text-right h-11"
                  size="large"
                />
              </Form.Item>

              {/* القاضي */}
              <Form.Item
                name="judge"
                label={
                  <span className="text-gray-800 font-semibold">القاضي</span>
                }
                className="mb-3"
              >
                <Input
                  prefix={<UserOutlined className="ml-2 text-primary" />}
                  placeholder="أدخل اسم القاضي"
                  className="text-right h-11"
                  size="large"
                />
              </Form.Item>

              {/* رقم القاعة */}
              <Form.Item
                name="room"
                label={
                  <span className="text-gray-800 font-semibold">
                    رقم القاعة
                  </span>
                }
                className="mb-3"
              >
                <Input
                  prefix={<HomeOutlined className="ml-2 text-primary" />}
                  placeholder="أدخل رقم القاعة"
                  className="text-right h-11"
                  size="large"
                />
              </Form.Item>
            </div>
          )}

          {/* حقول للجميع */}
          {/* 7. القرارات / نتائج الجلسة */}
          <Form.Item
            name="decisions"
            label={
              <span className="text-gray-800 font-semibold">
                {formData.session_type === "جلسة قضائية"
                  ? "القرارات"
                  : formData.session_type === "consultation"
                  ? "نتائج الاستشارة"
                  : "نتائج الاجتماع"}
              </span>
            }
            className="mb-3 md:col-span-2"
          >
            <TextArea
              prefix={<FileTextOutlined className="ml-2 text-primary" />}
              placeholder={`أدخل ${
                formData.session_type === "جلسة قضائية"
                  ? "قرارات الجلسة"
                  : formData.session_type === "consultation"
                  ? "نتائج الاستشارة"
                  : "نتائج الاجتماع"
              }`}
              className="text-right"
              rows={4}
            />
          </Form.Item>

          {/* 8. ملاحظات */}
          <Form.Item
            name="notes"
            label={<span className="text-gray-800 font-semibold">ملاحظات</span>}
            className="mb-3 md:col-span-2"
          >
            <TextArea
              prefix={<MessageOutlined className="ml-2 text-primary" />}
              placeholder="أدخل ملاحظات إضافية"
              className="text-right"
              rows={3}
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default AddSessionModal;
